import {
    Dispatch,
    ReactElement,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import {useSelector} from "react-redux";
import {
    FuelPurchase,
    FuelPurchaseRequestBody,
    TokenPaymentMethod,
} from "@devour/client";
import {StripePaymentMethodObject} from "@/types/Stripe";
import {IStore} from "@/redux/defaultStore";
import {useGetFuelBalance, formatFuelUsd} from "@/hooks/useGetFuelBalance";
import {NumericFormat} from "react-number-format";
import {formatNumberWithCommas} from "@/utils/formatNumberWithCommas";
import {AiOutlinePlus} from "react-icons/ai";
import {capitalize} from "lodash";
import {FaArrowRight, FaCheckCircle, FaRedoAlt} from "react-icons/fa";
import classNames from "classnames";
import {NumberFormatValues} from "react-number-format/types/types";
import SignUpToast from "@/components/SignUpToast";
import FuelTermsModal from "@/components/modals/FuelTermsModal";
import Toast from "@/components/Toast";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetUserLevel} from "@/hooks/useGetUserLevel";
import {minXpToLoadFuel as minXp} from "@/components/loadFuel/LoadFuelPanel";

/*
 * Mirrors the server's quote contract in `backend/src/routes/transactions/
 * upsert_fuel_purchase.ts` — the min/max it rejects with a FormError, and the
 * XP→USD rate it converts with. Used only to keep the displayed numbers live
 * between keystrokes; the server quote remains the authority on submit.
 */
const fiatMin = 5;
const fiatMax = 1500;
const xpUsd = 0.0001;
const requoteDebounceMs = 400;

interface Props {
    fetchPrice: (values: FuelPurchaseRequestBody) => Promise<void>;
    fuelPurchase: FuelPurchase;
    selectedPayment: StripePaymentMethodObject;
    setSelectedPayment: Dispatch<SetStateAction<StripePaymentMethodObject>>;
    setShowAddCardModal: Dispatch<SetStateAction<boolean>>;
    setShowConfirmModal: Dispatch<SetStateAction<boolean>>;
    disclaimerCheck: boolean;
    setDisclaimerCheck: Dispatch<SetStateAction<boolean>>;
    waitTimeCheck: boolean;
    setWaitTimeCheck: Dispatch<SetStateAction<boolean>>;
    formValues: FuelPurchaseRequestBody;
    setFormValues: Dispatch<SetStateAction<FuelPurchaseRequestBody>>;
    paymentMethods: Array<StripePaymentMethodObject>;
    countdown: string;
}

function LoadFuelSelectPayment(props: Props): ReactElement {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);
    const loadingIncrement = useSelector((store: IStore) => store.metaStore.loadingIncrement);

    const {
        data: transactionsData,
        isError: isTransactionsDataError,
        isLoading: isTransactionsLoading,
    } = useGetFuelBalance(fullToken, currentUser?.user?.id);
    const {data: userLevelData} = useGetUserLevel(fullToken, currentUser?.user?.id);
    const maxXp = userLevelData?.experienceGainedAtThisLevel || 0;

    const [showSignUpToast, setShowSignUpToast] = useState<boolean>(false);
    const [errorToastMessage, setShowErrorToastMessage] = useState<string>("");
    const [showCardList, setShowCardList] = useState<boolean>(false);
    const [showFuelTermsModal, setShowFuelTermsModal] = useState<boolean>(false);

    function openAddCardModal() {
        if (currentUser) {
            props.setShowAddCardModal(true);
        } else {
            setShowSignUpToast(true);
            setTimeout(() => {
                setShowSignUpToast(false);
            }, 4000);
        }
    }

    /*
     * Re-quote while the user types instead of waiting for blur. The server
     * quote is the authority, but it costs a round-trip (price oracle + upsert),
     * so the displayed FUEL/Total are recomputed locally from the quoted
     * `price` on every keystroke and the server call is debounced behind them.
     * Without this, typing a custom amount left the conversion block showing
     * the PREVIOUS amount's numbers until the field lost focus.
     */
    const requoteTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    function cancelPendingRequote(): void {
        if (requoteTimer.current) {
            clearTimeout(requoteTimer.current);
            requoteTimer.current = undefined;
        }
    }

    useEffect(() => cancelPendingRequote, []);

    function scheduleRequote(values: FuelPurchaseRequestBody): void {
        cancelPendingRequote();
        requoteTimer.current = setTimeout(() => {
            requoteTimer.current = undefined;
            void props.fetchPrice(values);
        }, requoteDebounceMs);
    }

    function fiatOnChange(values: NumberFormatValues): void {
        props.setFormValues({
            ...props.formValues,
            fiat: values.floatValue,
        });
        /*
         * Only auto-quote inside the server's accepted range. A mid-typing "2"
         * on the way to "20" would otherwise throw the backend's minimum-purchase
         * FormError, which drops the whole panel into the FUEL_ERROR state.
         * Out-of-range values still surface that error on blur, as before.
         */
        if (values.floatValue >= fiatMin && values.floatValue <= fiatMax) {
            scheduleRequote({fiat: values.floatValue});
        } else {
            cancelPendingRequote();
        }
    }

    function fiatOffFocus(): void {
        // Update quote with the new fiat amount.
        cancelPendingRequote();
        void props.fetchPrice({fiat: props.formValues.fiat});
    }

    function xpOnChange(values: NumberFormatValues): void {
        props.setFormValues({
            ...props.formValues,
            xp: values.floatValue,
        });
        if (values.floatValue >= minXp && values.floatValue <= maxXp) {
            scheduleRequote({xp: values.floatValue});
        } else {
            cancelPendingRequote();
        }
    }

    function xpOffFocus(): void {
        cancelPendingRequote();
        if (props.formValues.xp < minXp) {
            props.setDisclaimerCheck(false);
            setShowErrorToastMessage(`Your minimum amount of XP to convert to Fuel is ${minXp} XP. Please try again.`);
        } else if (props.formValues.xp > maxXp) {
            props.setDisclaimerCheck(false);
            setShowErrorToastMessage(`Your maximum amount of XP to convert to Fuel is ${maxXp} XP. Please try again.`);
        } else {
            // Update quote with new amounts
            void props.fetchPrice({xp: props.formValues.xp});
        }
    }

    function paymentMethodOnChange(value: TokenPaymentMethod): void {
        cancelPendingRequote();
        // Update quote with new amounts
        void props.fetchPrice({
            paymentMethod: value,
            fiat: value === TokenPaymentMethod.FIAT ? 25 : undefined,
            xp: value === TokenPaymentMethod.XP ? minXp : undefined,
        });
    }

    function onSelectPaymentMethod(paymentMethod: StripePaymentMethodObject): void {
        void props.fetchPrice({paymentMethodId: paymentMethod.id});
        props.setSelectedPayment(paymentMethod);
        setShowCardList(false); // Auto-collapse after selection
    }

    function dismissLoadWithXpErrorToast() {
        setShowErrorToastMessage("");
        void props.fetchPrice({
            xp: minXp,
        });
    }

    /*
     * Anchor-aligned render (devourplay-anchor/load-fuel-modal.html).
     * Classes namespaced under `.lfp2-*` so the visual styling lives in
     * `_load-fuel-panel.scss` next to the legacy `.buy-fuel-page_panel_*`
     * styles without colliding.
     *
     * Wires the same form callbacks (fiatOnChange / fiatOffFocus /
     * fetchPrice / paymentMethodOnChange / setShowConfirmModal) so the
     * existing Stripe + 3DS + fuel-purchase mutation pipeline stays intact.
     *
     * Removed from visible UI vs. the legacy layout:
     *   - "Current FUEL Price" oracle row (price baked into the conversion)
     *   - "Load with" FIAT/XP toggle (FIAT is the default; XP can be re-
     *     surfaced later as a payment-method option if product wants it)
     *   - Quote-expiry countdown text (anchor copy mentions fluctuation
     *     inline; the existing `countdown` prop is still wired for the
     *     refresh-quote flow that renders when the expiry kicks in)
     *
     * Disclaimers (`disclaimerCheck` + `waitTimeCheck`) are auto-set to
     * true on submit click — the equivalent disclosures are condensed
     * into the fine-print line under the CTA per the anchor.
     */

    const usdFmt = new Intl.NumberFormat("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    const wholeFmt = new Intl.NumberFormat("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 2});
    const chipAmounts = [10, 25, 50, 100] as const;
    const isXp = props.formValues.paymentMethod === TokenPaymentMethod.XP;
    const activeFiat = !isXp ? props.formValues.fiat : 0;
    const activeXp = isXp ? props.formValues.xp ?? 0 : 0;
    const matchedChip = chipAmounts.find(c => Math.abs((activeFiat ?? 0) - c) < 0.01) ?? null;

    /*
     * The server quote lags the input by a debounce + round-trip. Rather than
     * render last quote's numbers against this quote's amount, recompute FUEL
     * from the quoted `price` using the SAME formula the backend applies
     * (upsert_fuel_purchase.ts). `quoteIsStale` is true only in that gap.
     */
    const quotePrice = props.fuelPurchase?.price ?? 0;
    const quotedAmount = isXp ? props.fuelPurchase?.xp ?? 0 : props.fuelPurchase?.fiat ?? 0;
    const enteredAmount = (isXp ? activeXp : activeFiat) ?? 0;
    const quoteIsStale = quotePrice > 0 &&
        props.fuelPurchase?.paymentMethod === props.formValues.paymentMethod &&
        Math.abs(enteredAmount - quotedAmount) >= 0.005;

    const fuelDisplay = quoteIsStale
        ? Math.ceil((isXp ? enteredAmount * xpUsd : enteredAmount) / quotePrice)
        : props.fuelPurchase?.fuel ?? 0;
    const totalDisplay = quoteIsStale || props.fuelPurchase?.fiat == null
        ? activeFiat ?? 0
        : props.fuelPurchase.fiat;

    /*
     * XP path doesn't require a card; it requires a valid XP amount within range.
     * Submitting against a stale quote would charge an amount the user never
     * saw priced — confirm commits the quote server-side and charges it — so
     * the CTA stays locked until the server quote catches up with the entry.
     */
    const ctaDisabled = loadingIncrement > 0 || quoteIsStale || (isXp
        ? activeXp < minXp || activeXp > maxXp
        : !props.selectedPayment || (activeFiat ?? 0) < fiatMin || (activeFiat ?? 0) > fiatMax);

    function handleChipClick(usd: number): void {
        cancelPendingRequote();
        props.setFormValues({
            ...props.formValues,
            fiat: usd,
            paymentMethod: TokenPaymentMethod.FIAT,
        });
        void props.fetchPrice({
            fiat: usd,
            paymentMethod: TokenPaymentMethod.FIAT,
            paymentMethodId: props.selectedPayment?.id,
        });
    }

    function handleConfirmClick(): void {
        /*
         * Acknowledge disclosures inline with submit — the anchor surfaces
         * these as a single fine-print line under the CTA. Legal review may
         * re-introduce explicit checkboxes if required.
         */
        props.setDisclaimerCheck(true);
        props.setWaitTimeCheck(true);
        props.setShowConfirmModal(true);
    }

    return (
        <>
            <Toast
                variant="warning"
                message={errorToastMessage}
                isOpen={!!errorToastMessage}
                showButton={true}
                buttonMessage="Retry"
                forceWidth="min(560px, 92vw)"
                dismissOnClickOutside={true}
                remainIndefinitely={true}
                buttonFunction={dismissLoadWithXpErrorToast}
                onDismiss={dismissLoadWithXpErrorToast}
            />
            <SignUpToast
                message="Join DevourPlay to load Fuel!"
                isOpen={showSignUpToast}
                onDismiss={() => setShowSignUpToast(false)}
            />

            {/* HEADER — eyebrow + giant title + sub */}
            <div className="lfp2-eyebrow">Top up</div>
            <h2 className="lfp2-title">Load Fuel</h2>
            <p className="lfp2-sub">
                Add to your DevourPlay balance. Spend on Shop, Competition entries, and brand activations.
            </p>

            {/* CURRENT BALANCE */}
            {currentUser &&
                <div className="lfp2-balance">
                    <span className="lfp2-balance-lbl">Current Balance</span>
                    <div className="lfp2-balance-amounts">
                        <span className="lfp2-balance-fiat">
                            {isTransactionsLoading || !transactionsData
                                ? "—"
                                : isTransactionsDataError
                                    ? "Unavailable"
                                    : formatFuelUsd(transactionsData.balanceInUsd, {cents: true})}
                        </span>
                        <span className="lfp2-balance-fuel">
                            {transactionsData
                                ? `${formatNumberWithCommas(transactionsData.balance)} FUEL`
                                : "— FUEL"}
                        </span>
                    </div>
                </div>
            }

            {/* AMOUNT TO ADD */}
            <section className="lfp2-section">
                <div className="lfp2-section-lbl">Amount to add</div>

                {/* LOAD WITH — Credit card / XP toggle (restores XP→Fuel conversion) */}
                <div className="lfp2-method-toggle" role="tablist" aria-label="Load with">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={!isXp}
                        className={classNames("lfp2-method-toggle-opt", {"lfp2-method-toggle-opt--active": !isXp})}
                        onClick={() => paymentMethodOnChange(TokenPaymentMethod.FIAT)}
                        disabled={loadingIncrement > 0}
                    >
                        Credit card
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={isXp}
                        className={classNames("lfp2-method-toggle-opt", {"lfp2-method-toggle-opt--active": isXp})}
                        onClick={() => paymentMethodOnChange(TokenPaymentMethod.XP)}
                        disabled={loadingIncrement > 0}
                    >
                        XP
                    </button>
                </div>

                {!isXp &&
                    <>
                        <div className="lfp2-chips">
                            {chipAmounts.map(usd =>
                                <button
                                    key={usd}
                                    type="button"
                                    className={classNames("lfp2-chip", {"lfp2-chip--active": matchedChip === usd})}
                                    onClick={() => handleChipClick(usd)}
                                    disabled={loadingIncrement > 0}
                                >
                                    ${usd}
                                </button>)}
                        </div>

                        <label className="lfp2-custom">
                            <span className="lfp2-custom-lbl">Custom</span>
                            <span className="lfp2-custom-prefix">$</span>
                            <NumericFormat
                                value={props.formValues.paymentMethod === TokenPaymentMethod.FIAT ? props.formValues.fiat : ""}
                                onValueChange={fiatOnChange}
                                onBlur={fiatOffFocus}
                                decimalScale={2}
                                allowNegative={false}
                                thousandSeparator=","
                                /*
                                 * Never disabled on `loadingIncrement` — that counter is global and
                                 * ticks for any in-flight app request, so binding it here froze the
                                 * field mid-keystroke while its own debounced re-quote was running.
                                 */
                                placeholder="Enter amount"
                            />
                        </label>
                    </>
                }

                {isXp &&
                    <>
                        <label className="lfp2-custom">
                            <span className="lfp2-custom-lbl">XP to convert</span>
                            <NumericFormat
                                value={props.formValues.xp ?? ""}
                                onValueChange={xpOnChange}
                                onBlur={xpOffFocus}
                                decimalScale={0}
                                allowNegative={false}
                                thousandSeparator=","
                                placeholder={`Min ${minXp} XP`}
                            />
                            <span className="lfp2-custom-suffix">XP</span>
                        </label>
                        <div className="lfp2-xp-avail">Available: {formatNumberWithCommas(maxXp)} XP</div>
                    </>
                }

                {/* CONVERSION — anchor-style red-rail block */}
                <div className="lfp2-conversion">
                    <div className="lfp2-conversion-left">
                        <span className="lfp2-conversion-lbl">You&rsquo;ll receive</span>
                        <span className="lfp2-conversion-val">
                            {formatNumberWithCommas(fuelDisplay)}
                            <span className="lfp2-conversion-unit">FUEL</span>
                        </span>
                    </div>
                    <div className="lfp2-conversion-right">
                        Total<br />
                        <strong>
                            {isXp
                                ? `${formatNumberWithCommas(activeXp)} XP`
                                : `$${usdFmt.format(totalDisplay)}`}
                        </strong>
                    </div>
                </div>

                {props.countdown && props.fuelPurchase &&
                    <div className="lfp2-quote-note">
                        Quote valid for the next {props.countdown}
                    </div>
                }
            </section>

            {/* PAY WITH — card selection; XP conversion needs no card, so hide it. */}
            {!isXp &&
            <section className="lfp2-section">
                <div className="lfp2-section-lbl">Pay with</div>

                {!showCardList && props.selectedPayment
                    ? <div
                        className="lfp2-payment"
                        onClick={() => setShowCardList(true)}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="lfp2-payment-left">
                            <span className="lfp2-payment-card">
                                {capitalize(props.selectedPayment.card.brand).slice(0, 4)
                                    .toUpperCase()}
                            </span>
                            <div className="lfp2-payment-meta">
                                <span className="lfp2-payment-name">
                                    {capitalize(props.selectedPayment.card.brand)} •••• {props.selectedPayment.card.last4}
                                </span>
                                {props.selectedPayment.card.exp_month != null &&
                                    <span className="lfp2-payment-sub">
                                        Expires {String(props.selectedPayment.card.exp_month).padStart(2, "0")}/
                                        {String(props.selectedPayment.card.exp_year ?? "").slice(-2)}
                                        {currentUser?.user?.defaultPaymentMethodId === props.selectedPayment.id && " · Default"}
                                    </span>
                                }
                            </div>
                        </div>
                        <button
                            type="button"
                            className="lfp2-payment-change"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowCardList(true);
                            }}
                        >
                            Change
                        </button>
                    </div>
                    : <ul className="lfp2-payment-list">
                        {props.paymentMethods.map(pm =>
                            <li
                                key={pm.id}
                                className={classNames("lfp2-payment-list-item", {
                                    "lfp2-payment-list-item--active": props.selectedPayment?.id === pm.id,
                                })}
                                onClick={() => onSelectPaymentMethod(pm)}
                            >
                                <span className="lfp2-payment-card">
                                    {capitalize(pm.card.brand).slice(0, 4)
                                        .toUpperCase()}
                                </span>
                                <strong>{capitalize(pm.card.brand)} •••• {pm.card.last4}</strong>
                                {props.selectedPayment?.id === pm.id && <FaCheckCircle aria-hidden="true" />}
                            </li>)}
                        <li
                            className={classNames("lfp2-payment-list-item lfp2-payment-list-add", {
                                "lfp2-payment-list-item--disabled": !currentUser,
                            })}
                            onClick={() => openAddCardModal()}
                        >
                            <AiOutlinePlus aria-hidden="true" />
                            <strong>Add Credit Card</strong>
                        </li>
                    </ul>
                }
            </section>
            }

            {/* CTA — native <button> per the the legacy component retirement policy. */}
            <div className="lfp2-cta-zone">
                {props.countdown == null && props.fuelPurchase != null
                    ? <button
                        type="button"
                        className="lfp2-cta lfp2-cta--refresh"
                        onClick={() => props.fetchPrice(props.formValues)}
                    >
                        <span>Refresh Quote</span>
                        <FaRedoAlt aria-hidden="true" />
                    </button>
                    : <button
                        type="button"
                        className="lfp2-cta"
                        onClick={handleConfirmClick}
                        disabled={ctaDisabled}
                    >
                        <span>
                            {isXp
                                ? "Add to Balance"
                                : `Add $${wholeFmt.format(totalDisplay)} to Balance`}
                        </span>
                        <FaArrowRight aria-hidden="true" />
                    </button>
                }
                <p className="lfp2-cta-fine">
                    Stored in your DevourPlay balance · Value may change over time · Spend on Shop, Competitions, brand activations · {" "}
                    <button
                        type="button"
                        className="lfp2-terms-link"
                        onClick={() => setShowFuelTermsModal(true)}
                    >
                        Fuel terms
                    </button>
                </p>

                <FuelTermsModal
                    isOpen={showFuelTermsModal}
                    toggle={() => setShowFuelTermsModal(false)}
                />
            </div>
        </>
    );
}

export default LoadFuelSelectPayment;
